// src/features/chat/panel/PanelComposer.tsx
// The entity panel's composer: textarea with autogrow, paperclip file picker,
// pending-attachment chips with progress / named error / retry / remove.
// Uploads ride on the send (ChatDetailPage pattern): a failed upload aborts
// the send and keeps the draft, the chip offers retry.

import { useEffect, useRef, useState } from 'react';
import { useI18n } from '@react/hooks';
import { Button } from '@react/components';
import {
  formatFileSize,
  uploadAttachment,
  type PendingAttachment,
} from '@/features/chat/logic';
import { ghostIconBtn } from './panelShared';
import { ScheduleSendButton, ScheduledChip } from '../ScheduleSendControls';

let nextPendingId = 1;

export function PanelComposer({
  sending,
  onSend,
  textareaRef,
  editing,
  onSubmitEdit,
}: {
  sending: boolean;
  /**
   * Returns true when the message went out (or was scheduled) and the
   * composer should clear. `scheduledAt` set = store as scheduled instead
   * of sending now.
   */
  onSend: (content: string, attachmentIds: string[], scheduledAt?: Date | null) => Promise<boolean>;
  textareaRef?: React.RefObject<HTMLTextAreaElement>;
  /**
   * Edit mode: when set, the composer seeds its draft from the message,
   * hides attachments, and the submit button saves via onSubmitEdit.
   */
  editing?: { id: string; content: string } | null;
  /** Returns true when the edit saved and the composer should clear. */
  onSubmitEdit?: (content: string) => Promise<boolean>;
}) {
  const { t, locale } = useI18n();
  const [draft, setDraft] = useState('');
  const [pending, setPending] = useState<PendingAttachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [scheduledAt, setScheduledAt] = useState<Date | null>(null);
  const localRef = useRef<HTMLTextAreaElement>(null);
  const areaRef = textareaRef ?? localRef;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editingId = editing?.id || null;

  // Entering edit mode seeds the draft with the message's current content;
  // leaving it (or switching messages) resets.
  useEffect(() => {
    setDraft(editing?.content ?? '');
    const el = areaRef.current;
    if (el) {
      el.style.height = 'auto';
      if (editing) {
        el.focus();
        el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingId]);

  const growTextarea = () => {
    const el = areaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  };

  const addFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const additions: PendingAttachment[] = Array.from(files).map((file) => ({
      id: `pending-${nextPendingId++}`,
      file,
      previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined,
      progress: 0,
    }));
    setPending((prev) => [...prev, ...additions]);
  };

  const removePending = (id: string) => {
    setPending((prev) => {
      const item = prev.find((a) => a.id === id);
      if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl);
      return prev.filter((a) => a.id !== id);
    });
  };

  const uploadOne = async (att: PendingAttachment): Promise<string | null> => {
    if (att.entityId) return att.entityId;
    try {
      const { entityId } = await uploadAttachment(att.file, (progress) => {
        setPending((prev) => prev.map((a) => (a.id === att.id ? { ...a, progress } : a)));
      });
      setPending((prev) => prev.map((a) => (a.id === att.id ? { ...a, entityId, error: undefined } : a)));
      return entityId;
    } catch {
      setPending((prev) => prev.map((a) => (a.id === att.id ? { ...a, error: t('chat.entityPanel.uploadFailed') } : a)));
      return null;
    }
  };

  const retryUpload = (att: PendingAttachment) => {
    setPending((prev) => prev.map((a) => (a.id === att.id ? { ...a, error: undefined, progress: 0 } : a)));
    void uploadOne({ ...att, error: undefined, progress: 0 });
  };

  const send = async () => {
    const content = draft.trim();
    // Edit mode: text-only save, no upload dance.
    if (editingId) {
      if (!content || sending || !onSubmitEdit) return;
      const saved = await onSubmitEdit(content);
      if (saved) {
        setDraft('');
        const el = areaRef.current;
        if (el) el.style.height = 'auto';
      }
      return;
    }
    if ((!content && pending.length === 0) || sending || uploading) return;
    setUploading(true);
    try {
      // Upload everything not yet uploaded. One failure aborts the send —
      // the draft and the chips stay exactly as they are.
      const ids: string[] = [];
      for (const att of pending) {
        const id = await uploadOne(att);
        if (!id) return;
        ids.push(id);
      }
      const sent = await onSend(content, ids, scheduledAt);
      if (sent) {
        setDraft('');
        setScheduledAt(null);
        for (const att of pending) if (att.previewUrl) URL.revokeObjectURL(att.previewUrl);
        setPending([]);
        const el = areaRef.current;
        if (el) el.style.height = 'auto';
      }
    } finally {
      setUploading(false);
    }
  };

  const busy = sending || uploading;

  return (
    <div>
      {/* Armed schedule */}
      {!editingId && scheduledAt && (
        <ScheduledChip
          scheduledAt={scheduledAt}
          locale={locale}
          onClear={() => setScheduledAt(null)}
        />
      )}

      {/* Pending attachments */}
      {!editingId && pending.length > 0 && (
        <ul className="mb-2 flex flex-wrap gap-1.5">
          {pending.map((att) => (
            <li
              key={att.id}
              className={`group/chip relative flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs ${
                att.error ? 'border-destructive/40 bg-destructive/10' : 'border-border bg-muted/50'
              }`}
            >
              {att.previewUrl ? (
                <img src={att.previewUrl} alt="" className="h-6 w-6 rounded object-cover" />
              ) : (
                <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 shrink-0 text-muted-foreground" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l8.57-8.57A4 4 0 1118 8.84l-8.59 8.57a2 2 0 01-2.83-2.83l8.49-8.48" />
                </svg>
              )}
              <span className="max-w-[10rem] truncate text-foreground" title={att.file.name}>
                {att.file.name}
              </span>
              <span className="tabular-nums text-muted-foreground">{formatFileSize(att.file.size)}</span>
              {att.error ? (
                <button
                  type="button"
                  onClick={() => retryUpload(att)}
                  className="shrink-0 rounded px-1 text-destructive underline transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  title={att.error}
                >
                  {t('chat.entityPanel.retry')}
                </button>
              ) : !att.entityId && att.progress > 0 ? (
                <span className="tabular-nums text-muted-foreground">{att.progress}%</span>
              ) : null}
              <button
                type="button"
                onClick={() => removePending(att.id)}
                className="shrink-0 rounded p-0.5 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={t('chat.entityPanel.removeAttachment')}
              >
                <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden="true">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex items-end gap-1.5">
        {!editingId && (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className={`${ghostIconBtn} mb-0.5 shrink-0`}
            aria-label={t('chat.entityPanel.attach')}
            title={t('chat.entityPanel.attach')}
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l8.57-8.57A4 4 0 1118 8.84l-8.59 8.57a2 2 0 01-2.83-2.83l8.49-8.48" />
            </svg>
          </button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            addFiles(e.target.files);
            e.target.value = '';
          }}
        />
        <textarea
          ref={areaRef}
          value={draft}
          rows={1}
          onChange={(e) => {
            setDraft(e.target.value);
            growTextarea();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void send();
            }
          }}
          placeholder={t('chat.entityPanel.composerPlaceholder')}
          className="min-h-[2.5rem] flex-1 resize-none rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={t('chat.entityPanel.composerPlaceholder')}
        />
        <Button
          variant="primary"
          size="sm"
          isLoading={busy}
          disabled={(!draft.trim() && (editingId !== null || pending.length === 0)) || busy}
          onClick={() => void send()}
        >
          {editingId ? t('common.save') : t('chat.entityPanel.send')}
        </Button>
        {!editingId && (
          <ScheduleSendButton
            scheduledAt={scheduledAt}
            onSchedule={setScheduledAt}
            disabled={busy}
          />
        )}
      </div>
    </div>
  );
}
